import type { Citation } from '@/api/runs';
import { legacyToHtml } from '../answer/legacy';
import { sanitizeToFragment } from '../answer/sanitize';
import { streamingPrefix } from '../answer/streamingHtml';

/**
 * What Copy puts on the clipboard (spec §7): the answer, or one of its
 * tables, as sanitized HTML (tables, headings and bold survive a paste into
 * Word, cells into Excel) and as readable plain text. `[n]` citations stay
 * as text and a numbered source list follows.
 */

export interface CopyPayload {
  html: string;
  text: string;
}

export interface CopyInput {
  /** The answer as stored or streamed. */
  answer: string;
  /** Stored before chat runs (`format: null`). */
  legacy: boolean;
  /** Cut short (stopped, interrupted): an incomplete end is left out. */
  partial: boolean;
  citations: Citation[] | null;
  /** The whole answer, or its n-th table (0-based). */
  scope: { kind: 'answer' } | { kind: 'table'; index: number };
  labels: {
    /** The heading of the source list. */
    sources: string;
    /** A citation without a name. */
    unnamed: string;
  };
}

const CITATION = /\[(\d{1,4})\]/g;

const BLOCKS = new Set([
  'P',
  'H3',
  'H4',
  'UL',
  'OL',
  'LI',
  'BLOCKQUOTE',
  'PRE',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
]);

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** The `[n]` numbers that appear in the content, in order of first use. */
export const citedNumbers = (text: string): number[] => {
  const seen: number[] = [];
  for (const match of text.matchAll(CITATION)) {
    const n = Number(match[1]);
    if (!seen.includes(n)) seen.push(n);
  }
  return seen;
};

/** "[n] name · location" for each mapped citation, by number. */
export const sourceLines = (
  citations: Citation[],
  only: number[] | null,
  unnamed: string,
): string[] =>
  [...citations]
    .filter(citation => only == null || only.includes(Number(citation.n)))
    .sort((a, b) => Number(a.n) - Number(b.n))
    .map(citation =>
      [`[${citation.n}] ${citation.name ?? unnamed}`, citation.location ?? null]
        .filter(Boolean)
        .join(' · '),
    );

/** Readable plain text of sanitized answer HTML. */
export const htmlToText = (root: ParentNode): string => {
  const out: string[] = [];
  const newline = () => {
    if (out.length && !out[out.length - 1].endsWith('\n')) out.push('\n');
  };
  const walk = (node: Node, inPre: boolean) => {
    if (node.nodeType === 3) {
      const value = node.nodeValue ?? '';
      out.push(inPre ? value : value.replace(/\s+/g, ' '));
      return;
    }
    if (node.nodeType !== 1 && node.nodeType !== 11) return;
    const element = node as Element;
    const tag = element.nodeName;
    if (tag === 'BR') {
      out.push('\n');
      return;
    }
    if (tag === 'TABLE') {
      newline();
      for (const row of Array.from(element.querySelectorAll('tr'))) {
        const cells = Array.from(row.children)
          .filter(cell => cell.nodeName === 'TD' || cell.nodeName === 'TH')
          .map(cell =>
            htmlToText(cell)
              .replace(/\s*\n\s*/g, ' ')
              .trim(),
          );
        out.push(`${cells.join('\t')}\n`);
      }
      out.push('\n');
      return;
    }
    const block = BLOCKS.has(tag);
    if (block) newline();
    if (tag === 'LI') {
      const list = element.parentElement;
      if (list?.nodeName === 'OL') {
        const items = Array.from(list.children).filter(
          child => child.nodeName === 'LI',
        );
        out.push(`${items.indexOf(element) + 1}. `);
      } else {
        out.push('- ');
      }
    }
    if (tag === 'BLOCKQUOTE') out.push('> ');
    const pre = inPre || tag === 'PRE';
    for (const child of Array.from(node.childNodes)) {
      walk(child, pre);
    }
    if (block) newline();
    if (tag === 'P' || tag === 'H3' || tag === 'H4' || tag === 'TABLE') {
      out.push('\n');
    }
  };
  walk(root as unknown as Node, false);
  return out
    .join('')
    .split('\n')
    .map(line => line.replace(/[  ]+$/g, '').replace(/^ +/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Borders and padding Word and Excel keep (they ignore stylesheets). */
const styleForOffice = (root: DocumentFragment) => {
  for (const table of Array.from(root.querySelectorAll('table'))) {
    table.setAttribute('border', '1');
    table.setAttribute('cellpadding', '4');
    table.setAttribute('cellspacing', '0');
    table.setAttribute('style', 'border-collapse:collapse');
  }
  for (const cell of Array.from(root.querySelectorAll('th, td'))) {
    cell.setAttribute(
      'style',
      'border:1px solid #808080;padding:4px;vertical-align:top',
    );
  }
};

/** Build both clipboard formats. Returns null when there is nothing to copy. */
export const buildCopyPayload = (input: CopyInput): CopyPayload | null => {
  let source = input.legacy ? legacyToHtml(input.answer) : input.answer;
  if (input.partial) source = streamingPrefix(source);
  const fragment = sanitizeToFragment(source);
  const document = fragment.ownerDocument;

  let content: DocumentFragment = fragment;
  if (input.scope.kind === 'table') {
    const table = fragment.querySelectorAll('table')[input.scope.index];
    if (!table) return null;
    content = document.createDocumentFragment();
    content.append(table);
  }

  const bodyText = htmlToText(content);
  if (!bodyText) return null;

  // Only numbers the API mapped are sources; others stay plain text.
  const mapped = new Set((input.citations ?? []).map(c => Number(c.n)));
  const used = citedNumbers(content.textContent ?? '').filter(n =>
    mapped.has(n),
  );
  const lines = input.citations?.length
    ? sourceLines(
        input.citations,
        input.scope.kind === 'table' ? used : null,
        input.labels.unnamed,
      )
    : [];

  styleForOffice(content);
  const container = document.createElement('div');
  container.append(content);
  const sourcesHtml = lines.length
    ? `<p><strong>${escapeHtml(input.labels.sources)}</strong></p>${lines
        .map(line => `<p>${escapeHtml(line)}</p>`)
        .join('')}`
    : '';

  return {
    html: `<meta charset="utf-8"><div>${container.innerHTML}${sourcesHtml}</div>`,
    text: lines.length
      ? `${bodyText}\n\n${input.labels.sources}\n${lines.join('\n')}`
      : bodyText,
  };
};
